// Landing page for Audio Annotation Tool (for annotating emergency vehicle sounds such as sirens, horns, and maybe more in the future)

import { FC, useEffect } from 'react';
import { Button } from 'antd';
import CenteredBox from 'packages/lib/common-ui/centeredBox';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
  curSelectedRegionAtom,
  curTimeAtom,
  durationAtom,
  isPlayingAtom,
  userCanEditAtom,
} from 'packages/main/components/audio-tool/atoms';
import { timeToMinSecString } from 'packages/main/components/audio-tool/utils';
import type { Region } from 'wavesurfer.js/src/plugin/regions';
import { CloseOutlined } from '@ant-design/icons';
import {
  useDeselectCurRegion,
  usePausePlayAllAudio,
  useSeekAllToTime,
  useStopAllWavesurfers,
} from 'packages/main/components/audio-tool/audio-tool-event-handlers';
import { IGNORE_DESELECT_CLICK_CLASS_NAME } from 'packages/main/components/audio-tool/audio-tool-main';
import { stylesWR } from 'packages/main/components/audio-tool/audio-tool-wavesurfer-row';

// The following enables the 'css' prop. See: https://emotion.sh/docs/css-prop
/** @jsxImportSource @emotion/react */

const cssStyles = {
  whiteOnBlackButton: {
    '&:hover': {
      color: 'lightgray !important',
    },
    '&.ant-btn[disabled]': {
      color: 'gray !important',
    },
  },
};

const CONTROLS_PANEL_HEIGHT = '42px';

const sharedStyles: Record<string, React.CSSProperties> = {
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const styles: Record<string, React.CSSProperties> = {
  buttonContainer: {
    color: 'white',
    backgroundColor: 'black',
    borderColor: 'black',
  },
  playButton: { fontSize: '28px' },
  rootDiv: {
    width: '100%',
    height: '100%',
  },
  baseControlsContainer: {
    display: 'grid',
    gridTemplateColumns: '0.5fr 1fr 0.2fr',
    alignItems: 'center',
    width: '100%',
  },
  playerControls: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'center',
    height: '100%',
  },
  playerControlsContainer: {
    backgroundColor: 'black',
    color: 'white',
    borderRadius: '30px',
    width: '100%',
    height: CONTROLS_PANEL_HEIGHT,
  },
  playButtonAndTimeDisplay: {
    ...sharedStyles.flexRow,
    gap: '20px',
    width: '50%',
    height: '100%',
  },
  regionControlsContainer: {
    backgroundColor: 'black',
    color: 'white',
    borderRadius: '30px',
    width: '100%',
    height: CONTROLS_PANEL_HEIGHT,
  },
  regionControls: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'center',
    height: '100%',
  },
};

export const AudioToolControls: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const _isPlaying = useRecoilValue(isPlayingAtom);
  const _curTime = useRecoilValue<number>(curTimeAtom);
  const _duration = useRecoilValue<number>(durationAtom);
  const _curSelectedRegion = useRecoilValue(curSelectedRegionAtom);
  const _userCanEdit = useRecoilValue(userCanEditAtom);

  const deselectCurRegion = useDeselectCurRegion();
  const seekAllToTime = useSeekAllToTime();
  const stopAllWavesurfers = useStopAllWavesurfers();
  const pausePlayAllAudio = usePausePlayAllAudio();

  const handlePlayPauseButtonClick = useRecoilCallback(
    ({ snapshot, set }) =>
      async (): Promise<void> => {
        const curSelectedRegion = await snapshot.getPromise(
          curSelectedRegionAtom
        );
        if (curSelectedRegion) {
          await handleTogglePlayStopRegion(curSelectedRegion);
        } else {
          const myIsPlaying = await snapshot.getPromise(isPlayingAtom);
          await pausePlayAllAudio(!myIsPlaying);
        }
      }
  );

  const handleTogglePlayStopRegion = useRecoilCallback(
    ({ snapshot, set }) =>
      async (region: Region): Promise<void> => {
        const wasPlaying = await snapshot.getPromise(isPlayingAtom);

        await stopAllWavesurfers();

        // Glitch fix: For some reason, stopping playback resets the cursor to the start (0.0). Correct for this here.
        const regionStartPercent =
          region.start / region.wavesurfer.getDuration();
        await seekAllToTime(region.start, regionStartPercent);

        if (wasPlaying) {
          set(isPlayingAtom, false);
        } else {
          region.wavesurfer.setMute(false);
          set(isPlayingAtom, true);
          region.play();
        }
      }
  );

  const handleRemoveRegionButtonClick = useRecoilCallback(
    ({ snapshot, set }) =>
      async (region: Region): Promise<void> => {
        region.remove();
        set(curSelectedRegionAtom, null);
      }
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === ' ') {
        void handlePlayPauseButtonClick();
        // Glitch fix: if user had previously clicked on a Play/Pause button, it would be the current active element, and space would active it.
        // Since this keyDown handler also activates it, it would double-activate.
        e.preventDefault();
      }
      if (e.key === 'Escape') {
        void deselectCurRegion();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [deselectCurRegion, handlePlayPauseButtonClick]);

  return (
    <div style={{ ...stylesWR.wavesurferRow, height: '100%' }}>
      <div />
      {!_curSelectedRegion && (
        <CenteredBox>
          <div style={styles.playerControlsContainer}>
            <CenteredBox>
              <div style={styles.playButtonAndTimeDisplay}>
                <Button
                  css={cssStyles.whiteOnBlackButton}
                  style={{
                    ...styles.buttonContainer,
                    marginTop: 0,
                    marginBottom: '3px',
                  }}
                  icon={
                    <span className="material-icons" style={styles.playButton}>
                      {_isPlaying ? <>pause</> : <>play_arrow</>}
                    </span>
                  }
                  onClick={async (e): Promise<void> => {
                    await handlePlayPauseButtonClick();
                    (e.target as HTMLButtonElement).blur();
                  }}
                />
                <div>
                  {timeToMinSecString(_curTime)}
                  <span style={{ color: 'gray' }}>
                    <span style={{ margin: '8px' }}>/</span>
                    {timeToMinSecString(_duration)}
                  </span>
                </div>
              </div>
            </CenteredBox>
          </div>
        </CenteredBox>
      )}
      {_curSelectedRegion && (
        <CenteredBox>
          <div style={styles.regionControlsContainer}>
            <div
              style={styles.regionControls}
              className={IGNORE_DESELECT_CLICK_CLASS_NAME}
            >
              <div style={{ paddingLeft: '60px' }}>
                Annotated Region [{timeToMinSecString(_curSelectedRegion.start)}{' '}
                - {timeToMinSecString(_curSelectedRegion.end)}]
              </div>
              <CenteredBox>
                <Button
                  css={cssStyles.whiteOnBlackButton}
                  style={{
                    ...styles.buttonContainer,
                    marginTop: 0,
                    marginBottom: '6px',
                  }}
                  onClick={(): void => {
                    void handleTogglePlayStopRegion(_curSelectedRegion);
                  }}
                >
                  <div style={sharedStyles.flexRow}>
                    <div className="material-icons" style={styles.playButton}>
                      {_isPlaying ? <>pause</> : <>play_arrow</>}
                    </div>
                    <div>{_isPlaying ? 'Stop' : 'Play'} Annotation</div>
                  </div>
                </Button>
              </CenteredBox>
              <CenteredBox>
                <Button
                  css={cssStyles.whiteOnBlackButton}
                  style={{
                    ...styles.buttonContainer,
                    margin: '5px',
                  }}
                  icon={
                    <CloseOutlined
                      style={{ color: _userCanEdit ? 'red' : '#945354' }}
                    />
                  }
                  disabled={!_userCanEdit}
                  onClick={(): void =>
                    void handleRemoveRegionButtonClick(_curSelectedRegion)
                  }
                >
                  Remove Annotation
                </Button>
              </CenteredBox>
            </div>
          </div>
        </CenteredBox>
      )}
    </div>
  );
};
